import React, { PropsWithChildren } from "react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AxiosError } from "axios";
import { z } from "zod";
import { doReloadAuth } from "../utils/reloadAuth";

export const zServerAppError = z.object({
  error: z.object({
    id: z.string().optional(),
    type: z.string().optional(),
    message: z.string().optional(),
    sentry: z.string().optional(),
  }),
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60 * 24, // 24 hours
      retry: false, // Disable automatic retries
    },
  },
  queryCache: new QueryCache({
    onError: async (error) => {
      if (error instanceof AxiosError) {
        const parsedError = zServerAppError.safeParse(error.response?.data)

        // The Profile JWT expiry is copied from the Auth0 JWT
        // So, if either JWT has expired, we need to clear and reset both
        if (parsedError.success && ['jwtExpired', 'errProfileJwtExpired'].includes(parsedError.data.error.type ?? '')) {
          console.warn('Auth0 / Profile JWT Expired:', parsedError.data.error);

          // Reload the Auth0 Access and ID tokens, then update the Profile JWT
          // This has a backoff safeguard to prevent too many reloads in a short time
          await doReloadAuth()
        } else {
          console.error('Axios Error:', error.response?.data);
        }
      } else {
        console.error('Query Error:', error);
      }
    }
  })
});

const InitReactQuery: React.FC<PropsWithChildren> = ({ children }) =>
  <QueryClientProvider client={queryClient}>
    {children}
    <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
  </QueryClientProvider>

export default InitReactQuery
